import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * AuditStepper · "Cloud" edition — soft, calm, still big & clear
 * ---------------------------------------------------------------------
 * Understands YOUR payload verbatim (snake_case):
 *   stages/steps: [{ stage, label, status, completed_at, verified }]
 *     invite_sent → application_submitted → sanction_verification →
 *     identity_verification → manual_review → approved
 *
 * Design language:
 *   · Soft pastel washes + muted inks — colourful but gentle, never bold.
 *     No saturated fills. Depth comes from soft borders, gradients and
 *     shadows instead of colour intensity.
 *       completed   → soft sage green
 *       in_progress → orange-yellow amber  (gentle glow)
 *       pending     → quiet grey (open, dashed)
 *       rejected    → soft rose  ·  resubmit → muted gold outline
 *   · Big circular nodes, 5px rounded connector lines that softly colour in
 *     as progress moves, balanced non-overlapping labels + state line
 *     per node, and an always-open detail card for the current stage.
 *   · Brand #375AE7 kept as the header identity + progress ring.
 *   · Responsive: flowing single row ≥1000px (with responsive text wrapping
 *     and zero overlap even on tight viewports); vertical timeline on phones.
 *     Stamps render dd/mm/yyyy hh:mm.
 */

/* ------------------------------------------------------------------ */
/*  Date helper · dd/mm/yyyy hh:mm                                     */
/* ------------------------------------------------------------------ */

const MONTHS = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function parseDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const s = String(value).replace(/[·,]/g, ' ').trim().replace(/\s+/g, ' ');
  if (!s) return null;
  let m = s.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const [, d, mo, y, hh, mm, ss] = m;
    return new Date(+y, +mo - 1, +d, hh || 0, mm || 0, ss || 0);
  }
  m = s.match(
    /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const [, d, mon, y, hh, mm, ss] = m;
    const mo = MONTHS[mon.slice(0, 3).toLowerCase()];
    if (mo !== undefined)
      return new Date(+y, mo, +d, hh || 0, mm || 0, ss || 0);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
const pad2 = (n) => String(n).padStart(2, '0');

export function auditTime(value, fallback = '—') {
  const d = parseDate(value);
  if (!d) return fallback;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/* ------------------------------------------------------------------ */
/*  Stage icons  ·  six bespoke scenes                                 */
/* ------------------------------------------------------------------ */

const ICONS = {
  invite_sent: (
    <>
      {/* envelope */}
      <rect
        x="2.8"
        y="4.4"
        width="18.4"
        height="13.4"
        rx="2.4"
        fill="currentColor"
        opacity=".1"
      />
      <rect
        x="2.8"
        y="4.4"
        width="18.4"
        height="13.4"
        rx="2.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="m4.3 6.7 7.7 5.5 7.7-5.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity=".8"
      />
      {/* the invite on its way — small send plane lifting out */}
      <g className="a9-fly">
        <g transform="translate(9.8 8.2) scale(0.58)">
          <path
            d="m21.4 2.6-7.1 19.1-3.5-8.5-8.5-3.5 19.1-7.1Z"
            fill="currentColor"
          />
        </g>
      </g>
    </>
  ),
  application_submitted: (
    <>
      <path
        fill="currentColor"
        d="m21.4 2.6-7.1 19.1-3.5-8.5-8.5-3.5 19.1-7.1Z"
      />
      <path
        d="M21.4 2.6 10.8 13.2"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity=".55"
        strokeLinecap="round"
      />
    </>
  ),
  sanction_verification: (
    <>
      <circle cx="11.2" cy="12" r="7.9" fill="currentColor" opacity=".12" />
      <ellipse
        cx="11.2"
        cy="12"
        rx="3.6"
        ry="7.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity=".5"
      />
      <path
        d="M3.3 12h15.8"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity=".5"
      />
      <circle
        cx="11.2"
        cy="12"
        r="7.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <g className="a9-orbit">
        <circle
          cx="19.5"
          cy="5.9"
          r="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        />
      </g>
    </>
  ),
  identity_verification: (
    <>
      <path
        d="M4.6 7.4c0-.9.7-1.6 1.6-1.6h11.6c.9 0 1.6.7 1.6 1.6v9.2c0 .9-.7 1.6-1.6 1.6H6.2c-.9 0-1.6-.7-1.6-1.6V7.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity=".5"
      />
      <g
        stroke="currentColor"
        fill="none"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <path d="M7.8 12.2a4.2 4.2 0 0 1 8.4 0" />
        <path d="M9.6 12.2a2.4 2.4 0 0 1 4.8 0" />
        <path d="M6.9 8.9c1.5-2.1 3.3-3.1 5.4-3.1s3.9 1 5.4 3.1" opacity=".6" />
        <path
          d="M6.9 15.5c1.5 2.1 3.3 3.1 5.4 3.1s3.9-1 5.4-3.1"
          opacity=".6"
        />
      </g>
      <g className="a9-scan" stroke="currentColor" strokeLinecap="round">
        <path d="M5.6 10.9h2.7" strokeWidth="1.3" opacity=".7" />
      </g>
    </>
  ),
  manual_review: (
    <>
      <path
        d="M2.6 12C5.1 7.7 8.6 5.7 12 5.7s6.9 2 9.4 6.3c-2.5 4.3-6 6.3-9.4 6.3S5.1 16.3 2.6 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <circle
        cx="12"
        cy="11.9"
        r="3.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="11.9" r="1.6" fill="currentColor" />
    </>
  ),
  approved: (
    <>
      <g
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity=".45"
      >
        <path d="M12 2.6v1.8M12 19.6v1.8M3.8 6.8l1.6.9M18.6 16.3l1.6.9M2.6 12h1.8M19.6 12h1.8" />
      </g>
      <path
        d="M12 2.6 20 5.6v6.1c0 5.2-8 9.6-8 9.6s-8-4.4-8-9.6V5.6L12 2.6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m8.9 11.9 2.2 2.2 4.1-4.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
};

const FALLBACK = (
  <path
    fill="currentColor"
    d="M12 2.4 20.4 5.6v6c0 5.6-8.4 9.8-8.4 9.8S3.6 17.2 3.6 11.6v-6L12 2.4Z"
  />
);

const ALIAS = {
  invite_sent: 'invite_sent',
  'invite sent': 'invite_sent',
  invite: 'invite_sent',
  application_submitted: 'application_submitted',
  'application submitted': 'application_submitted',
  submitted: 'application_submitted',
  application: 'application_submitted',
  sanction_verification: 'sanction_verification',
  'sanction verification': 'sanction_verification',
  sanctions_verification: 'sanction_verification',
  'sanctions verification': 'sanction_verification',
  sanctions: 'sanction_verification',
  sanction: 'sanction_verification',
  identity_verification: 'identity_verification',
  'identity verification': 'identity_verification',
  identity: 'identity_verification',
  kyc: 'identity_verification',
  manual_review: 'manual_review',
  'manual review': 'manual_review',
  manual: 'manual_review',
  review: 'manual_review',
  approved: 'approved',
  approval: 'approved',
  approve: 'approved',
};

function resolveStageKey(step) {
  const raw = step ? (step.stage ?? step.id ?? step.label ?? '') : '';
  const asIs = String(raw).trim();
  return (
    ALIAS[asIs.toLowerCase().replace(/\s+/g, '_')] ||
    ALIAS[asIs.toLowerCase()] ||
    asIs.toLowerCase().replace(/\s+/g, '_')
  );
}
const stageIcon = (step) => ICONS[resolveStageKey(step)] || FALLBACK;

const STAGE_DESC = {
  invite_sent:
    'An invitation has been sent successfully. The customer can now start their application.',
  application_submitted:
    'The application has been received and queued for automated checks.',
  sanction_verification:
    'Checked against sanctions, PEP and other regulated lists (DFAT, UN, OFAC, EU).',
  identity_verification: 'Verified against identity and registry records.',
  manual_review:
    'A compliance analyst reviews the automated results and supporting documents.',
  approved:
    'The application passed every stage and an approval decision has been recorded.',
};
const FALLBACK_DESC =
  'Waiting for this stage to begin. Details appear as soon as processing starts.';
const stageDesc = (step) => STAGE_DESC[resolveStageKey(step)] || FALLBACK_DESC;

/* ------------------------------------------------------------------ */
/*  Status-aware description                                           */
/* ------------------------------------------------------------------ */
const PENDING_DESC = 'Waiting for this stage to begin.';
const COMPLETED_DESC = 'This stage has been completed successfully.';

function stageLine(step) {
  if (step?.note) return step.note;
  const st = statusOf(step);
  if (st === 'pending') return PENDING_DESC;
  if (st === 'completed') return COMPLETED_DESC;
  return stageDesc(step);
}

function whenMeta(step) {
  if (!step) return null;
  const k = (a, b) =>
    step[a] != null ? { label: b, value: auditTime(step[a]) } : null;
  return (
    k('completed_at', 'Completed') ||
    k('completedAt', 'Completed') ||
    k('date', 'Completed') ||
    k('timestamp', 'Completed') ||
    k('updated_at', 'Updated') ||
    k('updatedAt', 'Updated') ||
    null
  );
}

/* ------------------------------------------------------------------ */
/*  Soft pastel palette — gentle washes, muted inks                    */
/* ------------------------------------------------------------------ */

const BADGE = {
  completed: 'Completed',
  in_progress: 'In progress',
  rejected: 'Rejected',
  warning: 'Resubmit',
  pending: 'Pending',
};

const ROUND =
  'rounded-full flex items-center justify-center relative z-10 select-none';
const NODE_D = `w-[64px] h-[64px] lg:w-[72px] lg:h-[72px] ${ROUND}`;
const NODE_M = `w-[56px] h-[56px] sm:w-[60px] sm:h-[60px] ${ROUND}`;

const DOT = {
  completed: '#2E8E68',
  in_progress: '#E88A0C',
  rejected: '#A25664',
  warning: '#9A7512',
  pending: '#9AA5B6',
};

/* f = wash/gradient layer (no shape) — icon colour comes from text-* */
const LOOK = {
  completed: {
    f: 'text-[#2E8E68] bg-gradient-to-br from-[#EAF6EE] to-[#D9EFE0] border-[1.5px] border-[#BCDFC9] shadow-[0_14px_30px_-20px_rgba(46,142,104,.95)]',
    fill: false,
    label: 'text-[#1F6F50]',
    chip: 'bg-[#E0F1E7] text-[#1F6F50]',
    micro: 'text-[#3E7F60]',
    accent: 'bg-[#6EBB93]',
    connector: 'bg-gradient-to-r from-[#8FCDAF] to-[#E6F3EB]',
    rail: 'bg-gradient-to-b from-[#8FCDAF] to-[#E6F3EB]',
    halo: 'rgba(46,142,104,.12)',
    badgeInk: '#2E8E68',
    badgeRing: '#C9E7D5',
  },
  in_progress: {
    f: 'text-[#E88A0C] bg-gradient-to-br from-[#FFF5E1] to-[#FFE8BE] border-[1.5px] border-[#FBD889] shadow-[0_14px_30px_-16px_rgba(232,138,12,.55)]',
    fill: false,
    label: 'text-[#B35C00]',
    chip: 'bg-[#FFF0CF] text-[#B35C00]',
    micro: 'text-[#D57A08]',
    accent: 'bg-[#F7A933]',
    connector: 'bg-gradient-to-r from-[#F6BD55] to-[#FFF0D4]',
    rail: 'bg-gradient-to-b from-[#F6BD55] to-[#FFF0D4]',
    halo: 'rgba(232,138,12,.17)',
    badgeInk: '#E88A0C',
    badgeRing: '#FFDFA3',
  },
  rejected: {
    f: 'text-[#A25664] bg-gradient-to-br from-[#FBEEF0] to-[#F5E0E5] border-[1.5px] border-[#E8C4CB] shadow-[0_14px_30px_-20px_rgba(162,86,100,.85)]',
    fill: false,
    label: 'text-[#843D49]',
    chip: 'bg-[#F6E3E7] text-[#843D49]',
    micro: 'text-[#9A5A64]',
    accent: 'bg-[#D58B96]',
    connector: 'bg-gradient-to-r from-[#DFA6B1] to-[#F6E6E9]',
    rail: 'bg-gradient-to-b from-[#DFA6B1] to-[#F6E6E9]',
    halo: 'rgba(162,86,100,.1)',
    badgeInk: '#A25664',
    badgeRing: '#F0CED4',
  },
  warning: {
    f: 'text-[#9A7512] bg-gradient-to-br from-[#FBF4E1] to-[#F5E9C4] border-[1.5px] border-[#E5CE8B]',
    fill: false,
    label: 'text-[#7A610F]',
    chip: 'bg-[#F4E9C4] text-[#7A610F]',
    micro: 'text-[#A18028]',
    accent: 'bg-[#D3B358]',
    connector: 'bg-gradient-to-r from-[#DBC277] to-[#F3EACF]',
    rail: 'bg-gradient-to-b from-[#DBC277] to-[#F3EACF]',
    halo: 'rgba(154,117,18,.12)',
    badgeInk: '#9A7512',
    badgeRing: '#EAD99F',
  },
  pending: {
    f: 'text-[#9AA5B6] bg-gradient-to-br from-white to-[#F3F5F9] border-[1.5px] border-dashed border-[#D4DBE4]',
    fill: false,
    label: 'text-[#8A94A6]',
    chip: 'bg-[#EEF1F5] text-[#7C8798]',
    micro: 'text-[#A6B0BF]',
    accent: 'bg-[#C3CAD6]',
    connector: null,
    rail: null,
    halo: 'rgba(15,23,42,.05)',
    badgeInk: null,
    badgeRing: null,
  },
};

function statusOf(step) {
  const s = step && step.status ? step.status : 'pending';
  return [
    'completed',
    'in_progress',
    'rejected',
    'warning',
    'pending',
  ].includes(s)
    ? s
    : 'pending';
}

/* ------------------------------------------------------------------ */
/*  Motion CSS                                                         */
/* ------------------------------------------------------------------ */

const CSS = `
  .a9-card { animation: a9In .5s cubic-bezier(.22,1,.36,1) both; }
  .a9-col  { animation: a9Up .45s cubic-bezier(.22,1,.36,1) both; animation-delay: var(--d, 0ms); }
  .a9-node { transition: transform .25s cubic-bezier(.22,1,.36,1); -webkit-tap-highlight-color: transparent; }
  .a9-node:hover { transform: translateY(-3px); }
  .a9-node:active { transform: scale(.96); }
  .a9-pop  { animation: a9Pop .5s cubic-bezier(.34,1.5,.64,1) both; }
  .a9-draw { stroke-dasharray: 1; stroke-dashoffset: 1; animation: a9Draw .4s ease .2s forwards; }
  .a9-meta { animation: a9Up .35s cubic-bezier(.22,1,.36,1) .25s both; }
  .a9-fill { transform-origin: left center; animation: a9Grow .9s cubic-bezier(.22,1,.36,1) .05s both; }
  .a9-fillY{ transform-origin: center top; animation: a9GrowY .9s cubic-bezier(.22,1,.36,1) .05s both; }
  .a9-pulse{ animation: a9Pulse 1.8s ease-in-out infinite; }
  .a9-breathe{ animation: a9Breathe 2.6s ease-in-out infinite; }
  .a9-ring { animation: a9Ring 2s ease-out infinite; }
  .a9-spin { animation: a9Spin .85s linear infinite; }
  .a9-detail{ animation: a9Drop .32s cubic-bezier(.22,1,.36,1) both; }
  .a9-orbit { transform-origin: 11.2px 12px; animation: a9Orbit 2.6s linear infinite; }
  .a9-scan  { animation: a9Scan 1.4s ease-in-out infinite alternate; }
  .a9-fly   { animation: a9Fly 2.4s ease-in-out infinite; }

  @keyframes a9In    { from { opacity:0; transform: translateY(12px); } to { opacity:1; transform:none; } }
  @keyframes a9Up    { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform:none; } }
  @keyframes a9Pop   { 0% { transform: scale(.5); opacity:0; } 65% { transform: scale(1.1); opacity:1; } 100% { transform: scale(1); } }
  @keyframes a9Draw  { to { stroke-dashoffset: 0; } }
  @keyframes a9Grow  { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  @keyframes a9GrowY { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  @keyframes a9Pulse { 0%,100% { opacity:1; } 50% { opacity:.35; } }
  @keyframes a9Breathe { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(232,138,12,0); } 50% { transform: scale(1.02); box-shadow: 0 0 0 12px rgba(232,138,12,.08); } }
  @keyframes a9Ring  { 0% { transform: scale(.95); opacity:.45; } 100% { transform: scale(1.22); opacity:0; } }
  @keyframes a9Spin  { to { transform: rotate(360deg); } }
  @keyframes a9Drop  { from { opacity:0; transform: translateY(-5px); } to { opacity:1; transform:none; } }
  @keyframes a9Orbit { to { transform: rotate(360deg); } }
  @keyframes a9Scan  { from { transform: translateY(-5px); opacity:.15; } to { transform: translateY(5px); opacity:.85; } }
  @keyframes a9Fly   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-1.2px); } }

  @media (prefers-reduced-motion: reduce) {
    .a9-card,.a9-col,.a9-pop,.a9-meta,.a9-fill,.a9-fillY,.a9-pulse,.a9-breathe,
    .a9-ring,.a9-spin,.a9-detail,.a9-draw,.a9-node,.a9-orbit,.a9-scan,.a9-fly { animation:none !important; transition:none; }
  }
`;

/* ------------------------------------------------------------------ */
/*  Small decorative bits                                              */
/* ------------------------------------------------------------------ */

const ClockSvg = ({ size = 9 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="8.6" />
    <path d="M12 7.6V12l3 1.8" />
  </svg>
);

function StatusChip({ status, label }) {
  const lk = LOOK[status] || LOOK.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[9px] font-extrabold uppercase tracking-[.08em] ${lk.chip}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${status === 'in_progress' ? 'a9-pulse' : ''}`}
        style={{ background: DOT[status] || DOT.pending }}
      />
      {label}
    </span>
  );
}

/* soft corner badge */
function MiniMark({ status }) {
  if (status === 'completed')
    return (
      <span
        className="a9-pop absolute -right-1 -bottom-1 z-20 w-[22px] h-[22px] sm:w-[26px] sm:h-[26px] rounded-full bg-white text-[#2E8E68] ring-1 ring-[#C9E7D5] flex items-center justify-center shadow-[0_5px_12px_-6px_rgba(46,142,104,.55)]"
        aria-hidden="true"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
        >
          <path d="M5 12.6l4.4 4.4L19 7.4" className="a9-draw" />
        </svg>
      </span>
    );
  if (status === 'in_progress')
    return (
      <span
        className="a9-pop absolute -right-1 -bottom-1 z-20 w-[22px] h-[22px] sm:w-[26px] sm:h-[26px] rounded-full bg-white ring-1 ring-[#FFDFA3] flex items-center justify-center shadow-[0_5px_12px_-7px_rgba(222,128,8,.55)]"
        aria-hidden="true"
      >
        <span className="block w-[12px] h-[12px] sm:w-[13px] sm:h-[13px] rounded-full border-[2.5px] border-[#FFDFA3] border-t-[#E88A0C] a9-spin" />
      </span>
    );
  if (status === 'rejected')
    return (
      <span
        className="a9-pop absolute -right-1 -bottom-1 z-20 w-[22px] h-[22px] sm:w-[26px] sm:h-[26px] rounded-full bg-white text-[#A25664] ring-1 ring-[#F0CED4] flex items-center justify-center shadow-[0_5px_12px_-7px_rgba(132,61,73,.45)]"
        aria-hidden="true"
      >
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        >
          <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
        </svg>
      </span>
    );
  if (status === 'warning')
    return (
      <span
        className="a9-pop absolute -right-1 -bottom-1 z-20 w-[22px] h-[22px] sm:w-[26px] sm:h-[26px] rounded-full bg-white text-[#9A7512] ring-1 ring-[#EAD99F] flex items-center justify-center shadow-[0_5px_12px_-7px_rgba(122,97,15,.45)]"
        aria-hidden="true"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="4.2"
          strokeLinecap="round"
        >
          <path d="M12 5.4v7.8M12 18.4h.01" />
        </svg>
      </span>
    );
  return null;
}

/* coloured connector heading into a node */
function connectorOf(step) {
  const st = statusOf(step);
  const lk = LOOK[st];
  if (st === 'in_progress')
    return { cls: lk.connector, rail: lk.rail, w: '62%' };
  if (lk.connector) return { cls: lk.connector, rail: lk.rail, w: '100%' };
  return null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AuditStepper({
  steps = [],
  title = 'Customer Application Journey',
  onStepClick,
}) {
  const [picked, setPicked] = useState(null);
  const [shown, setShown] = useState(0);
  const ringRef = useRef(null);
  const gid = useMemo(() => (Math.random() + 1).toString(36).slice(2), []);

  const completed = useMemo(
    () => steps.filter((s) => statusOf(s) === 'completed').length,
    [steps],
  );
  const pct = steps.length ? Math.round((completed / steps.length) * 100) : 0;
  const activeIdx = useMemo(
    () => steps.findIndex((s) => statusOf(s) === 'in_progress'),
    [steps],
  );
  const lastDone = useMemo(() => {
    let last = -1;
    steps.forEach((s, i) => {
      if (statusOf(s) === 'completed') last = i;
    });
    return last;
  }, [steps]);

  const effective =
    picked != null && picked >= 0 && picked < steps.length
      ? picked
      : activeIdx >= 0
        ? activeIdx
        : lastDone >= 0
          ? lastDone
          : 0;
  const detailStep = steps[effective] || null;

  useEffect(() => {
    setPicked(null);
  }, [steps]);

  useEffect(() => {
    let raf;
    const from = shown;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 650);
      setShown(Math.round(from + (pct - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  useEffect(() => {
    const el = ringRef.current;
    if (!el) return;
    const C = 2 * Math.PI * 11;
    el.style.transition = 'none';
    el.style.strokeDasharray = C;
    el.style.strokeDashoffset = C;
    const id = requestAnimationFrame(() => {
      el.style.transition = 'stroke-dashoffset 750ms cubic-bezier(.22,1,.36,1)';
      el.style.strokeDashoffset = C * (1 - pct / 100);
    });
    return () => cancelAnimationFrame(id);
  }, [pct]);

  const pick = (idx) => {
    setPicked(picked === idx ? null : idx);
    if (onStepClick) onStepClick(steps[idx], idx);
  };

  if (!steps.length) return null;

  const circle = (step, index, mobile) => {
    const st = statusOf(step);
    const lk = LOOK[st];
    const isActive = st === 'in_progress';
    const isSel = effective === index;
    const sId = step.id || step.stage || index;
    const meta = whenMeta(step);
    return (
      <button
        type="button"
        onClick={() => pick(index)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pick(index);
          }
        }}
        aria-pressed={isSel}
        aria-label={`${step.label} — ${BADGE[st] || 'Pending'}`}
        tabIndex={0}
        title={`${step.label} — ${BADGE[st] || 'Pending'}${meta ? ` · ${meta.value}` : ''}`}
        className="a9-node relative z-10 outline-none cursor-pointer"
      >
        {isActive && (
          <span
            className="a9-ring absolute inset-0 rounded-full border-2 border-[#F7BE4F]/80"
            aria-hidden="true"
          />
        )}
        {isSel && !isActive && (
          <span
            className="absolute -inset-[6px] lg:-inset-[7px] rounded-full a9-pop"
            style={{ boxShadow: `0 0 0 6px ${lk.halo}` }}
            aria-hidden="true"
          />
        )}
        <span
          key={`${sId}-${st}${mobile ? '-m' : ''}`}
          className={`a9-pop ${mobile ? NODE_M : NODE_D} ${lk.f} ${isActive ? 'a9-breathe' : ''}`}
        >
          <span className="relative z-10 flex items-center justify-center">
            <svg
              className="w-[26px] h-[26px] lg:w-[33px] lg:h-[33px]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {stageIcon(step)}
            </svg>
          </span>
          <MiniMark status={st} />
        </span>
      </button>
    );
  };

  return (
    <div className="a9-card w-full rounded-[22px] bg-white border border-slate-200/90 shadow-[0_2px_4px_rgba(15,23,42,.03),0_34px_70px_-38px_rgba(15,23,42,.5)] overflow-hidden">
      <style>{CSS}</style>

      {/* ================= header ================= */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-[14px] bg-gradient-to-br from-[#375AE7] to-[#5F7DF2] text-white flex items-center justify-center shrink-0 shadow-[0_8px_18px_-8px_rgba(55,90,231,.8)]">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="8" cy="8.2" r="2.9" />
              <path d="M2.2 18.9c1-3.5 3.1-5.2 5.8-5.2s4.8 1.7 5.8 5.2" />
              <path d="M15.4 11.3l2.1 2.1 3.9-4.3" />
            </svg>
          </span>
          <div className="min-w-0 leading-tight">
            <h3 className="text-[15px] font-extrabold tracking-tight text-slate-900 truncate">
              {title}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-3.5 shrink-0">
          <span className="text-[18px] font-extrabold text-slate-800 tabular-nums tracking-tight">
            {completed}
            <span className="text-slate-300 font-bold">/{steps.length}</span>
          </span>
          <div className="relative w-10 h-10 shrink-0">
            <svg
              width="40"
              height="40"
              viewBox="0 0 28 28"
              className="-rotate-90"
            >
              <defs>
                <linearGradient
                  id={`ag-${gid}`}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#375AE7" />
                  <stop offset="100%" stopColor="#7B96FF" />
                </linearGradient>
              </defs>
              <circle
                cx="14"
                cy="14"
                r="11"
                fill="none"
                stroke="#EEF1F6"
                strokeWidth="3.2"
              />
              <circle
                ref={ringRef}
                cx="14"
                cy="14"
                r="11"
                fill="none"
                stroke={`url(#ag-${gid})`}
                strokeWidth="3.2"
                strokeLinecap="round"
              />
            </svg>
            <span
              key={shown}
              className="absolute inset-0 flex items-center justify-center text-[9.5px] font-extrabold text-slate-800 tabular-nums"
              style={{ animation: 'a9Pop .3s ease' }}
            >
              {shown}%
            </span>
          </div>
        </div>
      </div>

      {/* ================= desktop flowing row ================= */}
      {/* 
        Key fixes for small/medium desktop screens & tight containers:
        1. flex-1 with min-w-0 on each column.
        2. a9-col is constrained with w-full max-w-full min-w-0 so it NEVER spills into adjacent columns.
        3. Labels wrap cleanly up to 2 lines with break-words instead of overflowing or colliding.
        4. Label height is normalized (h-[30px] / h-[32px]) so all timestamps align on the exact same baseline.
        5. Timestamps wrap date & time gracefully on narrow columns without overlapping neighbors.
        6. Responsive node size & connectors adapt smoothly across 1000px–1400px+ viewports.
      */}
      <div className="hidden min-[1000px]:block px-3 sm:px-6 lg:px-8 pt-3 pb-2">
        <div className="flex items-start w-full">
          {steps.map((step, index) => {
            const conn = connectorOf(step);
            return (
              <div
                key={step.id || step.stage || index}
                className="relative flex-1 flex flex-col items-center min-w-0"
              >
                {index > 0 && (
                  <div
                    className="absolute z-0 h-[5px] rounded-full top-[29.5px] lg:top-[33.5px] left-[calc(-50%+32px)] lg:left-[calc(-50%+36px)] right-[calc(50%-32px)] lg:right-[calc(50%-36px)]"
                    aria-hidden="true"
                  >
                    <div className="absolute inset-0 bg-[#EBEEF3] rounded-full" />
                    {conn && (
                      <div
                        key={`${step.id || step.stage}-${statusOf(step)}`}
                        className={`a9-fill absolute inset-0 rounded-full ${conn.cls}`}
                        style={{ width: conn.w }}
                      />
                    )}
                  </div>
                )}
                <div
                  className="a9-col relative flex flex-col items-center w-full max-w-full min-w-0 px-0.5"
                  style={{ '--d': `${index * 90}ms` }}
                >
                  {circle(step, index, false)}

                  <div className="mt-2.5 lg:mt-3 text-center w-full max-w-full px-1 select-none flex flex-col items-center">
                    {/* Balanced 2-line label container: avoids single-line spillover and aligns timestamps */}
                    <div className="min-h-[28px] lg:min-h-[32px] w-full flex items-center justify-center">
                      <p
                        className={`text-[11px] min-[1100px]:text-[11.5px] min-[1250px]:text-[12.5px] leading-[1.2] font-bold tracking-tight text-center break-words line-clamp-2 ${lkOf(step).label}`}
                        title={step.label}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {step.label}
                      </p>
                    </div>

                    {/* State/timestamp line below label: perfectly aligned across all stages */}
                    <div className="min-h-[16px] lg:min-h-[18px] mt-[3px] flex items-center justify-center w-full max-w-full">
                      {stateLine(step)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================= phone vertical timeline ================= */}
      <div className="min-[1000px]:hidden px-4 sm:px-6 pt-2 pb-1">
        {steps.map((step, index) => {
          const st = statusOf(step);
          const lk = LOOK[st];
          const sId = step.id || step.stage || index;
          const last = index === steps.length - 1;
          const conn = connectorOf(step);
          const meta = whenMeta(step);
          return (
            <div key={sId} className="relative flex gap-4">
              <div
                className="relative flex flex-col items-center shrink-0"
                style={{ width: 60 }}
              >
                {circle(step, index, true)}
                {!last && (
                  <span
                    className="absolute top-[60px] sm:top-[64px] bottom-[-6px] w-[5px] rounded-full bg-[#EBEEF3] overflow-hidden"
                    aria-hidden="true"
                  >
                    {conn && (
                      <span
                        className={`absolute inset-0 ${conn.rail} a9-fillY`}
                        style={{ height: conn.w }}
                      />
                    )}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1 pt-[9px] sm:pt-[11px] pb-6 leading-tight">
                <div className="flex items-center gap-2 flex-wrap">
                  <p
                    className={`text-[13.5px] font-bold tracking-tight ${lk.label}`}
                  >
                    {step.label}
                  </p>
                  <StatusChip status={st} label={BADGE[st] || 'Pending'} />
                </div>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">
                  {stageLine(step)}
                </p>
                {meta && (
                  <p
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold mt-1 ${lk.micro}`}
                  >
                    <ClockSvg size={8.5} />{' '}
                    {meta.label === 'Completed' ? '' : `${meta.label} `}
                    {meta.value}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= always-open detail for current stage ================= */}
      {detailStep && (
        <div className="px-4 sm:px-6 pb-4 pt-3 border-t border-slate-100">
          <StepDetail
            step={detailStep}
            index={effective}
            total={steps.length}
            isPicked={picked != null}
            onDismiss={() => setPicked(null)}
          />
        </div>
      )}
    </div>
  );

  function lkOf(step) {
    return LOOK[statusOf(step)] || LOOK.pending;
  }

  function stateLine(step) {
    const st = statusOf(step);
    const lk = LOOK[st];
    const meta = whenMeta(step);

    if (st === 'in_progress')
      return (
        <span
          className={`a9-meta inline-flex items-center gap-1 sm:gap-1.5 text-[8.5px] sm:text-[9.5px] font-extrabold uppercase tracking-[.06em] sm:tracking-[.1em] whitespace-nowrap ${lk.micro}`}
        >
          <span
            className="w-1.5 h-1.5 rounded-full a9-pulse shrink-0"
            style={{ background: DOT.in_progress }}
          />{' '}
          In progress
        </span>
      );

    if (st === 'pending')
      return (
        <span
          className={`a9-meta inline-flex items-center gap-1 text-[8px] sm:text-[9px] font-bold uppercase tracking-[.06em] sm:tracking-[.1em] whitespace-nowrap ${lk.micro}`}
        >
          Pending
        </span>
      );

    if (meta) {
      // Split into date and time so tight columns can wrap naturally without colliding
      const valStr = String(meta.value || '').trim();
      const spaceIdx = valStr.lastIndexOf(' ');
      if (spaceIdx > 0) {
        const dPart = valStr.slice(0, spaceIdx);
        const tPart = valStr.slice(spaceIdx + 1);
        return (
          <span
            className={`a9-meta inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-0 text-[8px] min-[1100px]:text-[8.5px] min-[1250px]:text-[9.5px] font-bold leading-tight ${lk.micro}`}
          >
            <span className="inline-flex items-center gap-0.5 whitespace-nowrap shrink-0">
              <ClockSvg size={8} />
              <span>{dPart}</span>
            </span>
            <span className="whitespace-nowrap shrink-0 opacity-80">
              {tPart}
            </span>
          </span>
        );
      }
      return (
        <span
          className={`a9-meta inline-flex items-center gap-1 text-[8px] min-[1100px]:text-[8.5px] min-[1250px]:text-[9.5px] font-bold whitespace-nowrap ${lk.micro}`}
        >
          <ClockSvg size={8} /> {meta.value}
        </span>
      );
    }

    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Detail card                                                        */
/* ------------------------------------------------------------------ */

function StepDetail({ step, index, total, isPicked, onDismiss }) {
  const st = statusOf(step);
  const lk = LOOK[st];
  const meta = whenMeta(step);
  const desc = stageLine(step);

  const outcome =
    step.verified === true
      ? { ok: true, text: 'Verified' }
      : step.verified === false
        ? { ok: false, text: 'Discrepancy found' }
        : null;

  return (
    <div className="a9-detail relative flex items-start gap-4 rounded-2xl bg-gradient-to-r from-slate-50/80 to-white px-4 py-4">
      <span
        className={`absolute left-0 top-4 bottom-4 w-1 rounded-full ${lk.accent}`}
        aria-hidden="true"
      />
      <span
        className={`w-[60px] h-[60px] rounded-2xl flex items-center justify-center shrink-0 ${lk.f}`}
      >
        <svg width="29" height="29" viewBox="0 0 24 24" aria-hidden="true">
          {stageIcon(step)}
        </svg>
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[14px] font-extrabold text-slate-900">
            {step.label}
          </p>
          <StatusChip status={st} label={BADGE[st] || 'Pending'} />
          <span className="text-[10px] font-medium text-slate-300">
            Stage {index + 1} of {total}
          </span>
        </div>
        <p className="text-[11.5px] text-slate-500 mt-1 leading-snug line-clamp-2">
          {desc}
        </p>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {meta && (
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold ${lk.micro}`}
            >
              <ClockSvg size={9} />{' '}
              {meta.label === 'Completed' ? 'Completed' : meta.label}{' '}
              {meta.value}
            </span>
          )}
          {outcome && (
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-extrabold ${outcome.ok ? 'text-[#1F6F50]' : 'text-[#843D49]'}`}
            >
              {outcome.ok ? (
                <span className="w-4 h-4 rounded-full bg-[#E0F1E7] text-[#1F6F50] flex items-center justify-center">
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12.6l4.4 4.4L19 7.4" />
                  </svg>
                </span>
              ) : (
                <span className="w-4 h-4 rounded-full bg-[#F6E3E7] text-[#843D49] flex items-center justify-center">
                  <svg
                    width="7"
                    height="7"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4.2"
                    strokeLinecap="round"
                  >
                    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
                  </svg>
                </span>
              )}
              {outcome.text}
            </span>
          )}
        </div>
      </div>
      {isPicked && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Back to current stage"
          className="w-7 h-7 rounded-full flex items-center justify-center text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0 self-start -mr-1"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
